// ============================================================================
// ADMIN EVENT TYPES — Tier 1 CRUD for the studio session event type catalog
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Tag, Plus, Pencil, Loader, AlertCircle, X, CheckCircle, ToggleLeft, ToggleRight, Check
} from 'lucide-react';
import {
  listAllEventTypes,
  createEventType,
  updateEventType,
  EventType
} from '../services/studio-bookings';

function EventTypeRow({
  eventType, editingId, editName, editDescription, saving, togglingId,
  onEdit, onSave, onCancel, onToggle, setEditName, setEditDescription
}: {
  eventType: EventType;
  editingId: string | 'new' | null;
  editName: string;
  editDescription: string;
  saving: boolean;
  togglingId: string | null;
  onEdit: (et: EventType) => void;
  onSave: () => void;
  onCancel: () => void;
  onToggle: (et: EventType) => void;
  setEditName: (v: string) => void;
  setEditDescription: (v: string) => void;
}) {
  const isEditing = editingId === eventType.eventTypeId;

  if (isEditing) {
    return (
      <tr className="bg-primary/5">
        <td colSpan={3} className="px-5 py-3">
          <div className="flex gap-3 items-start">
            <div className="flex-1 space-y-2">
              <input
                autoFocus
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                placeholder="Event type name"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <div className="flex gap-2 flex-shrink-0 pt-0.5">
              <button
                onClick={onSave}
                disabled={!editName.trim() || saving}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold disabled:opacity-60 shadow-sm shadow-primary/20 hover:bg-primary/90 transition-all"
              >
                {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                onClick={onCancel}
                className="px-3 py-2 border border-[#e2e8f0] rounded-xl text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`hover:bg-slate-50/50 transition-colors ${!eventType.isActive ? 'opacity-50' : ''}`}>
      <td className="px-5 py-3.5">
        <p className="text-sm font-semibold text-navy">{eventType.name}</p>
      </td>
      <td className="px-5 py-3.5">
        <p className="text-sm text-slate-500">{eventType.description || '—'}</p>
      </td>
      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => onEdit(eventType)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onToggle(eventType)}
            disabled={togglingId === eventType.eventTypeId}
            className={`transition-colors ${eventType.isActive ? 'text-primary hover:text-primary/80' : 'text-slate-400 hover:text-slate-600'}`}
            title={eventType.isActive ? 'Deactivate' : 'Activate'}
          >
            {togglingId === eventType.eventTypeId
              ? <Loader className="w-4 h-4 animate-spin text-slate-400" />
              : eventType.isActive
                ? <ToggleRight className="w-5 h-5" />
                : <ToggleLeft className="w-5 h-5" />
            }
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminEventTypes() {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  function load() {
    setLoading(true);
    listAllEventTypes()
      .then(setEventTypes)
      .catch(() => setError('Failed to load event types'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  function startEdit(et: EventType) {
    setEditingId(et.eventTypeId);
    setEditName(et.name);
    setEditDescription(et.description || '');
  }

  function startCreate() {
    setEditingId('new');
    setEditName('');
    setEditDescription('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  }

  async function handleSave() {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      if (editingId === 'new') {
        await createEventType(editName.trim(), editDescription.trim() || undefined);
        setSuccess('Event type created');
      } else {
        await updateEventType(editingId!, { name: editName.trim(), description: editDescription.trim() || undefined });
        setSuccess('Event type updated');
      }
      cancelEdit();
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save event type');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(et: EventType) {
    setTogglingId(et.eventTypeId);
    try {
      await updateEventType(et.eventTypeId, { isActive: !et.isActive });
      setEventTypes(prev => prev.map(e => e.eventTypeId === et.eventTypeId ? { ...e, isActive: !e.isActive } : e));
    } catch {
      setError('Failed to update event type');
    } finally {
      setTogglingId(null);
    }
  }

  const active = eventTypes.filter(e => e.isActive);
  const inactive = eventTypes.filter(e => !e.isActive);

  const tableProps = {
    editingId, editName, editDescription, saving, togglingId,
    onEdit: startEdit, onSave: handleSave, onCancel: cancelEdit, onToggle: handleToggleActive,
    setEditName, setEditDescription
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Tag size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Event Types</h1>
            <p className="text-slate-500 text-sm font-medium">Manage the studio session event type catalog</p>
          </div>
        </div>
        {editingId !== 'new' && (
          <button
            onClick={startCreate}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add event type
          </button>
        )}
      </header>

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700 font-medium">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center h-40 items-center">
          <Loader className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <section>
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">
              Active <span className="font-normal normal-case text-slate-400">({active.length})</span>
            </p>
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/60">
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Description</th>
                    <th className="px-5 py-3 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {editingId === 'new' && (
                    <tr className="bg-primary/5">
                      <td colSpan={3} className="px-5 py-3">
                        <div className="flex gap-3 items-start">
                          <div className="flex-1 space-y-2">
                            <input
                              autoFocus
                              type="text"
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              placeholder="Event type name"
                              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                            />
                            <input
                              type="text"
                              placeholder="Description (optional)"
                              value={editDescription}
                              onChange={e => setEditDescription(e.target.value)}
                              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                            />
                          </div>
                          <div className="flex gap-2 flex-shrink-0 pt-0.5">
                            <button
                              onClick={handleSave}
                              disabled={!editName.trim() || saving}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold disabled:opacity-60 shadow-sm shadow-primary/20 hover:bg-primary/90 transition-all"
                            >
                              {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-2 border border-[#e2e8f0] rounded-xl text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {active.length === 0 && editingId !== 'new' ? (
                    <tr>
                      <td colSpan={3} className="px-5 py-8 text-center text-sm text-slate-400">
                        No active event types
                      </td>
                    </tr>
                  ) : (
                    active.map(et => (
                      <EventTypeRow key={et.eventTypeId} eventType={et} {...tableProps} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {inactive.length > 0 && (
            <section>
              <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">
                Inactive <span className="font-normal normal-case text-slate-400">({inactive.length})</span>
              </p>
              <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-slate-50/60">
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Description</th>
                      <th className="px-5 py-3 w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {inactive.map(et => (
                      <EventTypeRow key={et.eventTypeId} eventType={et} {...tableProps} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      </div>
    </div>
  );
}
